import { useCallback, useEffect, useRef, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

type PollingStatus = 'idle' | 'polling' | 'paused' | 'stopped';

interface UsePollingOptions {
  /** Function called on each poll tick. May be sync or async. */
  fn: () => void | Promise<void>;
  /** Interval between polls in milliseconds. Default: 5000 */
  intervalMs?: number;
  /** Start polling on mount. Default: true */
  enabled?: boolean;
  /** Fire one tick immediately before the first interval elapses. Default: true */
  immediate?: boolean;
  /** Pause while the browser tab is hidden. Default: true */
  pauseOnHidden?: boolean;
  /** Max consecutive errors before stopping. Default: 3 */
  maxRetries?: number;
  /** Called when maxRetries is exhausted */
  onMaxRetriesReached?: () => void;
}

interface UsePollingResult {
  status: PollingStatus;
  /** Manually trigger one tick immediately */
  poll: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  /** Restart polling from scratch */
  restart: () => void;
}

/**
 * Runs a function on a fixed interval.
 *
 * A thin wrapper around setTimeout chains — the hook governs timing and
 * retry policy. Data handling and stop conditions are the caller's
 * responsibility inside `fn`.
 *
 * - Accepts sync or async functions
 * - Ticks never overlap: the next tick starts only after `fn` settles
 * - Stops automatically after `maxRetries` consecutive thrown errors
 * - Pauses while the browser tab is hidden (opt-out via `pauseOnHidden: false`)
 *
 * @example
 * const { stop } = usePolling({
 *   fn: async () => {
 *     const data = await fetchJobStatus(jobId);
 *     setJobData(data);
 *     if (data.status === 'done') stop();
 *   },
 *   intervalMs: 3000,
 *   onMaxRetriesReached: () => setError('Polling failed'),
 * });
 * @return {UsePollingResult} Controls and current status of the polling loop.
 */
export function usePolling({
  fn,
  intervalMs = 5000,
  enabled = true,
  immediate = true,
  pauseOnHidden = true,
  maxRetries = 3,
  onMaxRetriesReached,
}: UsePollingOptions): UsePollingResult {
  const [status, setStatus] = useState<PollingStatus>(
    enabled ? 'polling' : 'idle'
  );

  // fnRef so the async tick always calls the latest fn without stale closures.
  const fnRef = useRef(fn);
  const onMaxRetriesReachedRef = useRef(onMaxRetriesReached);
  fnRef.current = fn;
  onMaxRetriesReachedRef.current = onMaxRetriesReached;

  // statusRef mirrors state so the async tick can read current status
  // without closing over a stale value.
  const statusRef = useRef<PollingStatus>(enabled ? 'polling' : 'idle');
  const consecutiveErrorsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A tick still in flight at unmount must not schedule another one.
  const mountedRef = useRef(true);
  // A tick in flight schedules the next one itself when it settles.
  const inFlightRef = useRef(false);
  // Bumped whenever a new loop starts (poll, resume, restart, re-enable), so a
  // tick still in flight from the old loop settles without scheduling another.
  const generationRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const updateStatus = useCallback((next: PollingStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  // Core tick — uses setTimeout chains so ticks never overlap.
  const tick = useCallback(async () => {
    if (statusRef.current !== 'polling') return;

    const generation = generationRef.current;
    const current = () => generation === generationRef.current;
    inFlightRef.current = true;
    try {
      await fnRef.current();
      if (current()) consecutiveErrorsRef.current = 0;
    } catch {
      if (!mountedRef.current || !current()) return;
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= maxRetries) {
        onMaxRetriesReachedRef.current?.();
        updateStatus('stopped');
        return;
      }
    } finally {
      if (current()) inFlightRef.current = false;
    }
    if (!current()) return;

    // While hidden the visibility handler schedules the next tick on show.
    const hidden = pauseOnHidden && document.hidden;
    if (statusRef.current === 'polling' && mountedRef.current && !hidden) {
      timerRef.current = setTimeout(tick, intervalMs);
    }
  }, [intervalMs, maxRetries, pauseOnHidden, updateStatus]);

  // Starts a new loop: whatever the old one had in flight will not schedule.
  const supersede = useCallback(() => {
    clearTimer();
    generationRef.current += 1;
    inFlightRef.current = false;
  }, [clearTimer]);

  const poll = useCallback(async () => {
    supersede();
    await tick();
  }, [supersede, tick]);

  const pause = useCallback(() => {
    if (statusRef.current !== 'polling') return;
    clearTimer();
    updateStatus('paused');
  }, [clearTimer, updateStatus]);

  const resume = useCallback(() => {
    if (statusRef.current !== 'paused') return;
    supersede();
    updateStatus('polling');
    timerRef.current = setTimeout(tick, intervalMs);
  }, [supersede, tick, intervalMs, updateStatus]);

  const stop = useCallback(() => {
    clearTimer();
    updateStatus('stopped');
  }, [clearTimer, updateStatus]);

  const restart = useCallback(() => {
    supersede();
    consecutiveErrorsRef.current = 0;
    updateStatus('polling');
    timerRef.current = setTimeout(tick, 0);
  }, [supersede, tick, updateStatus]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      if (statusRef.current === 'polling') {
        updateStatus('paused');
      }
    } else if (statusRef.current === 'paused' || statusRef.current === 'idle') {
      supersede();
      updateStatus('polling');
      if (immediate) {
        tick();
      } else {
        timerRef.current = setTimeout(tick, intervalMs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!pauseOnHidden) return;

    const handler = () => {
      if (document.hidden) {
        if (statusRef.current === 'polling') clearTimer();
      } else {
        if (statusRef.current === 'polling' && !inFlightRef.current) {
          clearTimer();
          timerRef.current = setTimeout(tick, intervalMs);
        }
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [clearTimer, tick, intervalMs, pauseOnHidden]);

  useEffect(() => {
    mountedRef.current = true;
    const unmount = () => {
      mountedRef.current = false;
      clearTimer();
    };
    if (!enabled) return unmount;

    if (immediate) {
      tick();
    } else {
      timerRef.current = setTimeout(tick, intervalMs);
    }

    return unmount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, poll, pause, resume, stop, restart };
}
